/**
 * useBudgetAlertNotifications — D52: bắn browser Notification khi 1 ngân sách
 * VỪA chạm ngưỡng cảnh báo (alerting) hoặc VỪA vượt hạn mức (over) trong phiên.
 *
 * Chống spam:
 *  - Lần chạy đầu (mount) chỉ "ghi nhận" trạng thái hiện có là đã-biết — KHÔNG bắn
 *    (tránh dội thông báo cho các ngân sách vốn đã quá hạn từ trước khi mở app).
 *  - Mỗi (budgetId, monthKey, level) chỉ bắn 1 lần.
 *  - "over" và "alert" là 2 level riêng → đi từ alert→over vẫn báo lần nữa (leo thang).
 *
 * Chỉ in-app (tab đang mở). Yêu cầu Notification.permission === "granted".
 */

import { useEffect, useRef } from "react";
import type { LifeOsApi } from "../lib/life-os-contract";
import { budgetStatuses, monthKeyOf } from "../bento/bento-derive";
import { todayIso } from "../lib/date";

export function useBudgetAlertNotifications(api: LifeOsApi): void {
  const known = useRef<Set<string>>(new Set());
  const seeded = useRef(false);

  const state = api.state;
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;

    const monthKey = monthKeyOf(todayIso());
    const statuses = budgetStatuses(state, monthKey);
    const keyOf = (id: string, level: "over" | "alert") => `${id}:${monthKey}:${level}`;

    // Seed: đánh dấu trạng thái hiện tại là đã-biết, không bắn lúc mở app.
    if (!seeded.current) {
      for (const s of statuses) {
        if (s.over) known.current.add(keyOf(s.budget.id, "over"));
        else if (s.alerting) known.current.add(keyOf(s.budget.id, "alert"));
      }
      seeded.current = true;
      return;
    }

    if (window.Notification.permission !== "granted") return;

    for (const s of statuses) {
      const level: "over" | "alert" | null = s.over ? "over" : s.alerting ? "alert" : null;
      if (!level) continue;
      const key = keyOf(s.budget.id, level);
      if (known.current.has(key)) continue;
      known.current.add(key);
      const title = level === "over" ? "🔴 Vượt ngân sách" : "⚠️ Sắp chạm hạn mức";
      const body =
        level === "over"
          ? `${s.budget.category}: đã chi vượt hạn mức tháng này.`
          : `${s.budget.category}: đã dùng ${s.percent}% hạn mức.`;
      new window.Notification(title, { body, tag: key });
    }
  }, [state]);
}
